import Icon from './Icon';
import { PRIORITY_LABELS, type IssuePriority } from '../types';

/**
 * A priority, as a mark and a word.
 *
 * The mark exists so priority is never carried by colour alone — the board's
 * standing rule, and the reason every status badge here already pairs a colour
 * with text. Red and orange are close enough on a small chip that somebody who
 * cannot separate them was reading nothing at all.
 *
 * ── CHEVRONS, NOT ARROWS — Vinnie, 10 Sep 2026 ──────────────────────────────
 *
 * It was a text arrow (↑ for high, ▲ for critical), and the arrow glyph did not
 * sit on the line — *"the up arrow is not inline … that does not look good."*
 * So the mark is now a stroked chevron drawn on the text baseline like every
 * other icon here: HIGH is one chevron up, CRITICAL two (more urgent, more
 * chevrons — the convention people already read on a lift button or a player).
 * Low is one chevron down; medium carries no mark, its word doing the work.
 *
 * It is one component rather than five copies because the chip is drawn in five
 * places — the card, the board's Next up panel, an open issue, and twice in
 * Insights — and a mark that appeared in four of them would look like a bug in
 * the fifth.
 *
 * The mark is hidden from screen readers: the word is right beside it, and a
 * chevron announced as "chevron up high" is worse than the word on its own.
 */

/** How many chevrons, and which way, for each rung. */
const MARK: Record<IssuePriority, { n: number; dir: 'up' | 'down' }> = {
  critical: { n: 2, dir: 'up' },
  high: { n: 1, dir: 'up' },
  medium: { n: 0, dir: 'up' },
  low: { n: 1, dir: 'down' },
};

/**
 * `markOnly` draws the chevron WITHOUT the word — the card face only (Vinnie,
 * "glyphs at the foot", 10 Oct 2026). A foot carrying a ref, a due date and an
 * owner cannot also fit the word on one board-column line, and the foot must be
 * one line (no wrap). The word is kept for a screen reader (`.sr-only`), so
 * priority is still announced; Medium has no chevron, so a Medium card shows no
 * visible priority mark on the face — its absence IS "ordinary". Everywhere with
 * room (the open card, Insights, Next up) keeps the word.
 */
/**
 * JUST THE MARK — the chevron(s) on their own, in the rung's colour, no word.
 * For a place that writes the word itself right beside the mark and would
 * otherwise say it twice: the card's Priority dropdown (PickField), whose option
 * and trigger already carry the label, so the glyph ties the menu to the board
 * the same way the board's own chips do. Medium carries no chevron, so it draws
 * nothing — its absence is "ordinary", exactly as on the board face. Hidden from
 * screen readers; the label beside it is the announced text.
 */
export function PriorityGlyph({ priority }: { priority: IssuePriority }) {
  const { n, dir } = MARK[priority];
  return (
    <span className={`prio prio-${priority}`} aria-hidden="true">
      <span className={`prio-mark${n === 2 ? ' is-double' : ''}`}>
        {n === 0 ? (
          // Medium sits LEVEL — neither up nor down — so in the menu it carries a
          // short horizontal stroke in the same weight as the chevrons: the mid
          // rung of the up / level / down family. Without it Medium was a blank
          // slot that left the row's label out of line with the others and read
          // as unfinished (Vinnie, 17 Oct). The board's own priority chip still
          // leaves Medium unmarked — "ordinary" — this mark is the dropdown's.
          <svg width="13" height="13" viewBox="0 0 24 24" className="prio-level" aria-hidden="true">
            <path d="M6 12h12" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
          </svg>
        ) : (
          Array.from({ length: n }).map((_, i) => (
            <Icon key={i} name="chevron" size={13} className={`prio-chev prio-chev-${dir}`} />
          ))
        )}
      </span>
    </span>
  );
}

export default function Priority({
  priority,
  markOnly = false,
}: {
  priority: IssuePriority;
  markOnly?: boolean;
}) {
  const { n, dir } = MARK[priority];
  return (
    <span className={`prio prio-${priority}${markOnly ? ' prio-markonly' : ''}`}>
      {n > 0 && (
        <span className={`prio-mark${n === 2 ? ' is-double' : ''}`} aria-hidden="true">
          {Array.from({ length: n }).map((_, i) => (
            <Icon key={i} name="chevron" size={13} className={`prio-chev prio-chev-${dir}`} />
          ))}
        </span>
      )}
      {markOnly ? <span className="sr-only">{PRIORITY_LABELS[priority]}</span> : PRIORITY_LABELS[priority]}
    </span>
  );
}
